import { type KeyboardEvent, useMemo, useState } from "react";
import { BookOpen, Globe, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useDocuments } from "@/hooks/documents/useDocumentFetch";
import { MAX_MEDIA_SOURCE_DOMAINS, MEDIA_SOURCE_OPTIONS, normalizeDomain } from "@/lib/agent/mediaSources";
import type { MediaSourceMode, MediaSourcesConfig } from "@/types/agent/agent";

const WEBSITE_SOURCE_TYPES = ["website", "website-scrape"];

interface MediaSourcesFieldProps {
  agentId?: string;
  value: MediaSourcesConfig;
  onChange: (value: MediaSourcesConfig) => void;
  brand: string;
}

/**
 * Where "Show Links & Images" may look after the genie's own content: the
 * whole web, a list of approved sites, or nowhere else.
 */
export const MediaSourcesField = ({ agentId, value, onChange, brand }: MediaSourcesFieldProps) => {
  const [draft, setDraft] = useState("");
  const [draftError, setDraftError] = useState<string | null>(null);

  const approvedOnly = value.mode === "approved_sites";
  const domains = value.allowed_domains;
  const slots = MAX_MEDIA_SOURCE_DOMAINS - domains.length;

  const { data: documents = [] } = useDocuments("my-documents", { agentId }, undefined, approvedOnly && !!agentId);

  // The sites the genie already learned from are the likeliest ones to approve.
  const suggestions = useMemo(() => {
    const hosts = documents
      .filter((doc) => WEBSITE_SOURCE_TYPES.includes(doc.source_type ?? ""))
      .map((doc) => normalizeDomain(doc.metadata?.url ?? doc.source_path))
      .filter((host): host is string => !!host);
    return [...new Set(hosts)].filter((host) => !domains.includes(host));
  }, [documents, domains]);

  const setMode = (mode: MediaSourceMode) => onChange({ ...value, mode });

  const addDomains = (entries: string[]) => {
    const added = entries.map(normalizeDomain).filter((domain): domain is string => !!domain);
    onChange({
      ...value,
      allowed_domains: [...new Set([...domains, ...added])].slice(0, MAX_MEDIA_SOURCE_DOMAINS),
    });
  };

  // A pasted list is taken in one go; anything that isn't a site stays in the box to be fixed.
  const handleAddDraft = () => {
    const entries = draft.split(/[\s,;]+/).filter(Boolean);
    if (entries.length === 0) return;
    const invalid = entries.filter((entry) => !normalizeDomain(entry));
    addDomains(entries);
    setDraft(invalid.join(", "));
    setDraftError(invalid.length > 0 ? "Enter a site like yourcompany.com" : null);
  };

  const handleDraftKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    handleAddDraft();
  };

  const removeDomain = (domain: string) =>
    onChange({ ...value, allowed_domains: domains.filter((entry) => entry !== domain) });

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 animate-in fade-in slide-in-from-top-1 duration-300">
      <div className="text-sm font-medium text-gray-900">Where Show Links & Images can look</div>
      <p className="text-xs text-gray-500 mt-0.5">Your genie always checks your own content first.</p>

      <div className="mt-3 space-y-2" role="radiogroup" aria-label="Where Show Links & Images can look">
        {MEDIA_SOURCE_OPTIONS.map((option) => {
          const isSelected = value.mode === option.mode;
          return (
            <label
              key={option.mode}
              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                isSelected
                  ? "bg-genie-primary-light/60 border-genie-primary/40"
                  : "bg-white border-gray-200 hover:bg-gray-50"
              }`}
            >
              <input
                type="radio"
                name={`media-sources-${agentId ?? "new"}`}
                checked={isSelected}
                onChange={() => setMode(option.mode)}
                className="mt-0.5 border-gray-300 text-genie-primary focus:ring-genie-primary"
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium text-gray-900">{option.label}</div>
                <div className="text-xs text-gray-500 mt-0.5">{option.description}</div>
              </div>
            </label>
          );
        })}
      </div>

      {approvedOnly && (
        <div className="mt-3 animate-in fade-in slide-in-from-top-1 duration-300">
          <div className="flex items-center gap-2 mb-2">
            <Globe className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Approved sites</span>
            <span className="text-xs text-gray-400">
              ({domains.length}/{MAX_MEDIA_SOURCE_DOMAINS})
            </span>
          </div>

          {domains.length > 0 ? (
            <div className="space-y-2 mb-3">
              {domains.map((domain) => (
                <div
                  key={domain}
                  className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white pl-3 pr-1"
                >
                  <span className="min-w-0 flex-1 truncate text-sm text-gray-900">{domain}</span>
                  <button
                    type="button"
                    onClick={() => removeDomain(domain)}
                    className="flex min-h-[44px] min-w-[44px] sm:min-h-[36px] sm:min-w-[36px] flex-shrink-0 items-center justify-center text-gray-400 hover:text-red-500 transition-colors"
                    aria-label={`Remove ${domain}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 mb-3">
              Add at least one site. Pages on its subdomains, such as shop.yourcompany.com, are included.
            </p>
          )}

          {slots > 0 ? (
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <input
                  type="text"
                  inputMode="url"
                  value={draft}
                  onChange={(event) => {
                    setDraft(event.target.value);
                    setDraftError(null);
                  }}
                  onKeyDown={handleDraftKeyDown}
                  placeholder="yourcompany.com"
                  aria-label="Site to approve"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base sm:text-sm"
                />
                {draftError && <p className="text-xs text-red-600 mt-1">{draftError}</p>}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={handleAddDraft}
                disabled={!draft.trim()}
                className="min-h-[44px] sm:min-h-0 flex-shrink-0 gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                Add
              </Button>
            </div>
          ) : (
            <p className="text-xs text-gray-500">Maximum of {MAX_MEDIA_SOURCE_DOMAINS} sites reached</p>
          )}

          {slots > 0 && suggestions.length > 0 && (
            <div className="mt-3">
              <div className="flex items-center gap-1.5 text-xs font-medium text-gray-700 mb-2">
                <BookOpen className="w-3.5 h-3.5 text-genie-primary" />
                From your knowledge base
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((host) => (
                  <button
                    key={host}
                    type="button"
                    onClick={() => addDomains([host])}
                    className="inline-flex min-h-[44px] sm:min-h-0 items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                    style={{ backgroundColor: `${brand}15`, color: brand }}
                  >
                    <Plus className="w-3 h-3" />
                    {host}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
